import React, { useEffect, useMemo, useState } from 'react'
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material'
import {
    ArrowBack as ArrowBackIcon,
    Favorite as FavoriteIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useTransactionsSafe } from '../contexts/TransactionsContext'

const ORANGE = '#FF8A00'
const SURFACE = 'rgba(16,11,38,0.92)'
const SURFACE_SOFT = 'rgba(255,255,255,0.03)'
const BORDER = '1px solid rgba(255,255,255,0.08)'
const DIASPORA_CODES = ['US', 'CA', 'UK', 'GB', 'AU', 'FR', 'EU']

const fallbackCountries = [
    {
        code: 'SL',
        name: 'Sierra Leone',
        currency: 'SLE',
        causes: ['School Fees Support', 'Hospital Care Fund', 'Food Relief'],
    },
    {
        code: 'LR',
        name: 'Liberia',
        currency: 'LRD',
        causes: ['Community Health Drive', 'Girls Education Fund', 'Emergency Support'],
    },
    {
        code: 'GN',
        name: 'Guinea',
        currency: 'GNF',
        causes: ['Youth Education Fund', 'Maternal Care Support', 'Local Food Program'],
    },
    {
        code: 'GM',
        name: 'Gambia',
        currency: 'GMD',
        causes: ['Village School Fund', 'Women Traders Relief', 'Medical Support'],
    },
]

const normalizeDonationCountryCode = (value) => {
    const country = String(value || '').trim().toUpperCase()
    if (['SI', 'SLE', 'SL', 'SIERRA LEONE'].includes(country)) return 'SL'
    if (['LIB', 'LRD', 'LR', 'LIBERIA'].includes(country)) return 'LR'
    if (['GIN', 'GNF', 'GN', 'GUINEA'].includes(country)) return 'GN'
    if (['GMB', 'GMD', 'GM', 'GAMBIA'].includes(country)) return 'GM'
    return ''
}

const countriesFromDonationPrograms = (programs) => {
    if (!Array.isArray(programs) || programs.length === 0) return null

    const causesByCountry = fallbackCountries.reduce((carry, country) => {
        carry[country.code] = []
        return carry
    }, {})

    programs.forEach((program) => {
        const name = String(program?.name || '').trim()
        if (!name) return

        const code = normalizeDonationCountryCode(program?.country_code)
        if (!code) {
            Object.keys(causesByCountry).forEach((entryCode) => {
                if (!causesByCountry[entryCode].includes(name)) {
                    causesByCountry[entryCode].push(name)
                }
            })
            return
        }

        if (causesByCountry[code] && !causesByCountry[code].includes(name)) {
            causesByCountry[code].push(name)
        }
    })

    return fallbackCountries.map((country) => ({
        ...country,
        causes: causesByCountry[country.code].length > 0 ? causesByCountry[country.code] : country.causes,
    }))
}

const ratesToUsd = {
    USD: 1,
    GBP: 1.27,
    CAD: 0.74,
    AUD: 0.66,
    EUR: 1.09,
    SLE: 0.0421,
    LRD: 0.0052,
    GNF: 0.000115,
    GMD: 0.0145,
}

const currencySymbols = {
    USD: '$',
    GBP: '£',
    CAD: 'C$',
    AUD: 'A$',
    EUR: '€',
    SLE: 'SLE ',
    LRD: 'LRD ',
    GNF: 'GNF ',
    GMD: 'GMD ',
}

const fieldSx = {
    '& .MuiInputBase-root': { bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 4, color: '#fff' },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.62)' },
    '& .MuiInputLabel-root.Mui-focused': { color: ORANGE },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,138,0,0.38)' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
}

const money = (amount, currency) => {
    const numeric = Number(amount || 0)
    const symbol = currencySymbols[currency] || `${currency} `
    if (['USD', 'GBP', 'CAD', 'AUD', 'EUR'].includes(currency)) {
        try {
            return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(numeric)
        } catch {
            return `${currency} ${numeric.toFixed(2)}`
        }
    }
    return `${symbol}${numeric.toFixed(2)}`
}

const row = (label, value, highlight = false) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.62)', fontWeight: 700 }}>{label}</Typography>
        <Typography sx={{ color: highlight ? ORANGE : '#fff', fontWeight: highlight ? 900 : 800, textAlign: 'right' }}>{value}</Typography>
    </Box>
)

export default function Donations() {
    const navigate = useNavigate()
    const transactionsContext = useTransactionsSafe()
    const savedUser = JSON.parse(localStorage.getItem('session_user') || '{}')
    const senderCurrency = String(savedUser?.baseCurrency || 'USD').toUpperCase()
    const isDiaspora = DIASPORA_CODES.includes(String(savedUser?.country || '').toUpperCase())
    const residentCountryCode = normalizeDonationCountryCode(savedUser?.country) || 'SL'

    const [countries, setCountries] = useState(fallbackCountries)
    const [loadingPrograms, setLoadingPrograms] = useState(false)
    const [countryCode, setCountryCode] = useState(residentCountryCode)
    const [cause, setCause] = useState(fallbackCountries[0].causes[0])
    const [donorName, setDonorName] = useState('')
    const [impactAmount, setImpactAmount] = useState('')
    const [message, setMessage] = useState('')
    const [anonymous, setAnonymous] = useState(false)
    const [showReceipt, setShowReceipt] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [receipt, setReceipt] = useState(null)

    const selectedCountry = useMemo(
        () => countries.find((country) => country.code === countryCode) || countries[0],
        [countries, countryCode]
    )

    useEffect(() => {
        let alive = true
        setLoadingPrograms(true)

        api.get('/donation-programs/active')
            .then((response) => {
                if (!alive) return
                const nextCountries = countriesFromDonationPrograms(
                    response.data?.data || response.data?.programs || []
                )
                if (!nextCountries) return

                setCountries(nextCountries)
                const selected =
                    nextCountries.find((country) => country.code === residentCountryCode) ||
                    nextCountries[0]
                setCountryCode(selected.code)
                setCause(selected.causes[0])
            })
            .catch(() => {
                // Keep built-in causes if the admin API is temporarily unavailable.
            })
            .finally(() => {
                if (alive) setLoadingPrograms(false)
            })

        return () => {
            alive = false
        }
    }, [residentCountryCode])

    const pricing = useMemo(() => {
        const amount = Number(impactAmount || 0)
        if (amount <= 0) {
            return {
                localAmount: 0,
                senderAmount: 0,
                fee: 0,
                totalDebit: 0,
                exchangeRate: 1,
            }
        }

        if (!isDiaspora) {
            const fee = Number(Math.max(amount * 0.0035, 1).toFixed(2))
            return {
                localAmount: amount,
                senderAmount: amount,
                fee,
                totalDebit: Number((amount + fee).toFixed(2)),
                exchangeRate: 1,
            }
        }

        const usdValue = Number((amount * (ratesToUsd[selectedCountry.currency] || 1)).toFixed(4))
        const senderAmount = Number((usdValue / (ratesToUsd[senderCurrency] || 1)).toFixed(2))
        const fee = Number(Math.max(senderAmount * 0.018, 1.25).toFixed(2))
        const totalDebit = Number((senderAmount + fee).toFixed(2))
        const exchangeRate = amount > 0 && senderAmount > 0 ? Number((amount / senderAmount).toFixed(4)) : 1

        return {
            localAmount: amount,
            senderAmount,
            fee,
            totalDebit,
            exchangeRate,
        }
    }, [impactAmount, isDiaspora, selectedCountry.currency, senderCurrency])

    const handleDonate = async () => {
        const amount = Number(impactAmount || 0)
        if (submitting || amount <= 0) return

        setSubmitting(true)
        try {
            const response = await api.post('/donations', {
                program_name: cause,
                country_code: countryCode,
                amount: pricing.localAmount,
                wallet_charged: pricing.totalDebit,
                sender_currency: senderCurrency,
                receiver_currency: selectedCountry.currency,
                sender_amount: pricing.senderAmount,
                transfer_fee: pricing.fee,
                exchange_rate: pricing.exchangeRate,
                anonymous,
                metadata: {
                    donor_name: anonymous ? 'Anonymous' : donorName.trim(),
                    donor_note: message.trim(),
                    pricing_note: isDiaspora ? 'diaspora_remittance' : 'local_wallet',
                },
            })

            const donation = response.data?.donation || {}
            const reference =
                response.data?.transaction_reference ||
                donation.transaction_ref ||
                donation.donation_no ||
                `DON-${Date.now()}`
            const createdAt = donation.created_at || new Date().toISOString()

            transactionsContext?.addTransaction?.({
                id: reference,
                createdAt,
                type: 'donation',
                status: 'completed',
                amount: pricing.totalDebit,
                fee: pricing.fee,
                currency: senderCurrency,
                counterpart: cause,
                note: 'Donation',
                raw: {
                    ...donation,
                    service_domain: 'donations',
                    payment_category: 'donation',
                    transaction_reference: reference,
                    proof_reference: donation.donation_no,
                },
            })
            transactionsContext?.loadFromApi?.().catch(() => {})

            setReceipt({ donation, reference, createdAt })
            setShowReceipt(true)
        } catch (error) {
            alert(error?.response?.data?.message || error?.message || 'Donation failed. Please try again.')
        } finally {
            setSubmitting(false)
        }
    }

    const done = () => {
        setShowReceipt(false)
        transactionsContext?.loadFromApi?.().catch(() => {})
        navigate('/transactions')
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#09061B', py: { xs: 4, md: 6 } }}>
            <Container maxWidth="md">
                {!showReceipt ? (
                    <>
                        <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 2 }}>
                            <Button
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate('/dashboard')}
                                sx={{ color: '#fff', textTransform: 'none', fontWeight: 700 }}
                            >
                                Back
                            </Button>
                        </Stack>

                        <Card
                            sx={{
                                borderRadius: 6,
                                mb: 3,
                                overflow: 'hidden',
                                background: 'linear-gradient(135deg, rgba(34,18,82,0.98) 0%, rgba(91,46,255,0.94) 62%, rgba(255,138,0,0.18) 100%)',
                                border: BORDER,
                            }}
                        >
                            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 1.1, fontWeight: 700 }}>Give Across Borders</Typography>
                                <Typography sx={{ color: '#fff', fontSize: { xs: 30, md: 42 }, fontWeight: 950 }}>
                                    Donate
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.80)', mt: 1.2, maxWidth: 760 }}>
                                    Support education, health, and community causes in Africa while paying from your resident wallet.
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                                    <Chip label="Cross-border giving" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 800 }} />
                                    <Chip label="Local impact amount" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 800 }} />
                                    <Chip label="Digital record" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', fontWeight: 800 }} />
                                </Stack>
                            </CardContent>
                        </Card>

                        <Card sx={{ borderRadius: 6, bgcolor: SURFACE, border: BORDER }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 22 }}>Choose Where Support Goes</Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.70)', mt: 0.8, mb: 2.5 }}>
                                    {loadingPrograms
                                        ? 'Loading active donation causes from MojoPayr.'
                                        : 'Select a receiving country and cause. The amount shown is the local impact on the ground.'}
                                </Typography>

                                <Stack spacing={2}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Country"
                                        value={countryCode}
                                        onChange={(event) => {
                                            const nextCode = event.target.value
                                            const nextCountry = countries.find((country) => country.code === nextCode) || countries[0]
                                            setCountryCode(nextCode)
                                            setCause(nextCountry.causes[0])
                                        }}
                                        sx={fieldSx}
                                    >
                                        {countries.map((country) => (
                                            <MenuItem key={country.code} value={country.code}>
                                                {country.name} ({country.currency})
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        select
                                        fullWidth
                                        label="Cause"
                                        value={cause}
                                        onChange={(event) => setCause(event.target.value)}
                                        sx={fieldSx}
                                    >
                                        {selectedCountry.causes.map((entry) => (
                                            <MenuItem key={entry} value={entry}>
                                                {entry}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField fullWidth label="Donor Name" placeholder="Enter your name" value={donorName} onChange={(event) => setDonorName(event.target.value)} sx={fieldSx} />
                                    <TextField fullWidth label={`Impact Amount (${selectedCountry.currency})`} placeholder="Enter local donation amount" value={impactAmount} onChange={(event) => setImpactAmount(event.target.value)} sx={fieldSx} />
                                    <TextField fullWidth label="Message (optional)" placeholder="Leave a short note" value={message} onChange={(event) => setMessage(event.target.value)} sx={fieldSx} />

                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography sx={{ color: '#fff', fontWeight: 800 }}>Donate anonymously</Typography>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.62)' }}>Hide donor name on the donation record.</Typography>
                                        </Box>
                                        <Switch checked={anonymous} onChange={(event) => setAnonymous(event.target.checked)} />
                                    </Stack>

                                    <Card sx={{ borderRadius: 5, bgcolor: SURFACE_SOFT, border: BORDER }}>
                                        <CardContent sx={{ p: 2.6 }}>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.55 }}>
                                                {isDiaspora
                                                    ? `Diaspora detected from your resident profile. Your donation is charged in ${senderCurrency} and applied as ${selectedCountry.currency} support for ${selectedCountry.name} with remittance-style FX and fees.`
                                                    : `Local resident detected from your profile. Your donation stays in your local wallet rail with lighter pricing to keep MojoPayr affordable for daily giving.`}
                                            </Typography>
                                        </CardContent>
                                    </Card>

                                    <Card sx={{ borderRadius: 5, bgcolor: SURFACE_SOFT, border: BORDER }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mb: 2 }}>Donation Review</Typography>
                                            <Stack spacing={1.25}>
                                                {row('Cause', cause)}
                                                {row('Impact', money(pricing.localAmount, selectedCountry.currency))}
                                                {row('You Give', money(pricing.senderAmount, senderCurrency))}
                                                {row('Platform Fee', money(pricing.fee, senderCurrency))}
                                                {row('FX Rate', `1 ${senderCurrency} = ${pricing.exchangeRate.toFixed(4)} ${selectedCountry.currency}`)}
                                                <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.10)', pt: 1.4 }}>
                                                    {row('Total Debit', money(pricing.totalDebit, senderCurrency), true)}
                                                </Box>
                                            </Stack>
                                        </CardContent>
                                    </Card>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        disabled={submitting || Number(impactAmount || 0) <= 0}
                                        onClick={handleDonate}
                                        sx={{ borderRadius: 999, py: 1.35, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE, '&:hover': { bgcolor: '#ff9c2a' } }}
                                    >
                                        {submitting ? 'Recording donation...' : 'Donate Now'}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </>
                ) : (
                    <Card sx={{ borderRadius: 6, border: BORDER, bgcolor: SURFACE }}>
                        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    overflow: 'hidden',
                                    background: 'linear-gradient(135deg, rgba(34,18,82,0.98) 0%, rgba(91,46,255,0.94) 62%, rgba(255,138,0,0.18) 100%)',
                                    border: BORDER,
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <FavoriteIcon sx={{ color: '#fff', fontSize: 34 }} />
                                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 20, mt: 2 }}>Donation Receipt</Typography>
                                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: 34, mt: 1 }}>{money(pricing.localAmount, selectedCountry.currency)}</Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.74)', mt: 1.2 }}>
                                        Your donation has been logged with a digital proof trail.
                                    </Typography>
                                </CardContent>
                            </Card>

                            <Card sx={{ mt: 2.5, borderRadius: 5, bgcolor: SURFACE_SOFT, border: BORDER }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack spacing={1.25}>
                                        {row('Reference', receipt?.reference || '-')}
                                        {row('Type', 'Donation')}
                                        {row('Status', 'COMPLETED')}
                                        {row('Date', receipt?.createdAt ? new Date(receipt.createdAt).toLocaleString() : new Date().toLocaleString())}
                                        {row('Provider', cause)}
                                        {row('Description', anonymous ? 'Anonymous' : donorName || 'MojoPayr Donor')}
                                        {row('Account', message || 'Thank you')}
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card sx={{ mt: 2.5, borderRadius: 5, bgcolor: SURFACE_SOFT, border: BORDER }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Stack spacing={1.25}>
                                        {row('Receiver Amount', money(pricing.localAmount, selectedCountry.currency))}
                                        {row('Base Amount', money(pricing.senderAmount, senderCurrency))}
                                        {row('Fee', money(pricing.fee, senderCurrency))}
                                        {row('Total Paid', money(pricing.totalDebit, senderCurrency), true)}
                                        {row('Rate', `1 ${senderCurrency} = ${pricing.exchangeRate.toFixed(2)} ${selectedCountry.currency}`)}
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Card sx={{ mt: 2.5, borderRadius: 5, bgcolor: SURFACE_SOFT, border: BORDER }}>
                                <CardContent sx={{ p: 2.6 }}>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.6 }}>
                                        This proof is stored in MojoPayr activity so users can track money movement with a clearer audit trail.
                                    </Typography>
                                </CardContent>
                            </Card>

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={done}
                                sx={{ mt: 3, borderRadius: 999, py: 1.35, textTransform: 'none', fontWeight: 900, bgcolor: ORANGE, '&:hover': { bgcolor: '#ff9c2a' } }}
                            >
                                Done
                            </Button>
                        </CardContent>
                    </Card>
                )}
            </Container>
        </Box>
    )
}
